// FILE: useProviderModelCatalog.ts
// Purpose: Single source for everything the provider/model picker needs to render.
// Layer: Web hooks
// Exports: useProviderModelCatalog, useVisibleManagedAgents,
//          useVisibleManagedAgentsState, mergeDynamicModelOptions.
//
// The picker appears in two places that resolve models identically — the chat
// composer and the "New thread" dialog — so the static options + SDK-reported
// models + per-user allowlist merge lives here rather than in either caller.

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import type { ManagedAgent, ProviderKind, ServerProviderStatus } from "@taskin-code/contracts";

import { normalizeModelSlug } from "@taskin-code/shared/model";

import { managedAgentsQueryOptions } from "~/agents/managedAgentsReactQuery";
import { getCustomModelOptionsByProvider, useAppSettings } from "~/appSettings";
import { useAuthStore } from "~/auth/authStore";
import { providerModelsQueryOptions } from "~/lib/providerDiscoveryReactQuery";
import { serverConfigQueryOptions } from "~/lib/serverReactQuery";

const EMPTY_PROVIDER_STATUSES: ServerProviderStatus[] = [];

export interface ModelOption {
  slug: string;
  name: string;
  isCustom?: boolean;
}

function formatModelSlug(slug: string): string {
  return slug
    .replace(/^gpt-/i, "GPT-")
    .replace(/^claude-/i, "Claude ")
    .replace(/-/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function mergeDynamicModelOptions(input: {
  provider: ProviderKind;
  staticOptions: ReadonlyArray<ModelOption>;
  dynamicModels: ReadonlyArray<{ slug: string; name?: string | null }>;
}): ReadonlyArray<ModelOption> {
  const staticNameBySlug = new Map(input.staticOptions.map((model) => [model.slug, model.name]));
  const dynamicNormalizedSlugs = new Set<string>();
  const normalizedDynamicOptions: Array<{ slug: string; name: string }> = [];

  for (const dynamicModel of input.dynamicModels) {
    const rawName = dynamicModel.name?.trim() ?? "";
    const isClaudeDefaultAlias =
      input.provider === "claudeAgent" &&
      (rawName.toLowerCase() === "default (recommended)" ||
        rawName.toLowerCase() === "default recommended" ||
        dynamicModel.slug.trim().toLowerCase() === "default");
    if (isClaudeDefaultAlias) {
      continue;
    }

    const normalizedSlug =
      normalizeModelSlug(dynamicModel.slug, input.provider) ?? dynamicModel.slug;
    if (dynamicNormalizedSlugs.has(normalizedSlug)) {
      continue;
    }
    dynamicNormalizedSlugs.add(normalizedSlug);
    normalizedDynamicOptions.push({
      slug: normalizedSlug,
      name:
        staticNameBySlug.get(normalizedSlug) ??
        dynamicModel.name ??
        formatModelSlug(normalizedSlug),
    });
  }

  const customOnlyModels = input.staticOptions.filter(
    (model) => "isCustom" in model && model.isCustom && !dynamicNormalizedSlugs.has(model.slug),
  );

  return [...normalizedDynamicOptions, ...customOnlyModels];
}

export interface ProviderModelCatalog {
  modelOptionsByProvider: Record<ProviderKind, ReadonlyArray<ModelOption>>;
  visibleManagedAgents: ReadonlyArray<ManagedAgent>;
  providerStatuses: ReadonlyArray<ServerProviderStatus>;
}

/**
 * The managed agents the current user may pick.
 *
 * `GET /api/agents` already applies the personal allowlist server-side, so the
 * filter here is belt-and-braces for a stale query result rather than the gate.
 * An empty set means "no personal policy" and must not filter anything — that is
 * the case for every account that predates the feature.
 *
 * Split out of `useProviderModelCatalog` because the model-selection policy
 * needs the agent list without the model options, and re-deriving the filter at
 * that call site would let the two answers drift.
 */
export function useVisibleManagedAgentsState(): {
  readonly agents: ReadonlyArray<ManagedAgent>;
  readonly isLoading: boolean;
} {
  const allowedAgentIds = useAuthStore((state) => state.allowedAgentIds);
  const managedAgentsQuery = useQuery(managedAgentsQueryOptions());

  const agents = useMemo(() => {
    const loaded = managedAgentsQuery.data ?? [];
    if (allowedAgentIds.size === 0) return loaded;
    return loaded.filter((agent) => allowedAgentIds.has(agent.id));
  }, [managedAgentsQuery.data, allowedAgentIds]);

  return { agents, isLoading: managedAgentsQuery.isLoading };
}

/**
 * The agent list alone, for callers that have nothing different to show while it
 * loads — an empty list and a pending one lead to the same render there.
 */
export function useVisibleManagedAgents(): ReadonlyArray<ManagedAgent> {
  return useVisibleManagedAgentsState().agents;
}

/**
 * Resolve the model options, managed agents and provider statuses backing the
 * provider/model picker. All three queries are shared react-query entries, so
 * mounting this in more than one component costs nothing extra.
 */
export function useProviderModelCatalog(): ProviderModelCatalog {
  const { settings } = useAppSettings();
  const allowedModelKeys = useAuthStore((state) => state.allowedModelKeys);
  const claudeDynamicModelsQuery = useQuery(
    providerModelsQueryOptions({ provider: "claudeAgent" }),
  );
  const codexDynamicModelsQuery = useQuery(providerModelsQueryOptions({ provider: "codex" }));
  const visibleManagedAgents = useVisibleManagedAgents();
  const serverConfigQuery = useQuery(serverConfigQueryOptions());

  const modelOptionsByProvider = useMemo(() => {
    const staticOptions = getCustomModelOptionsByProvider(settings);
    const result = { ...staticOptions };

    const dynamicSources: Record<ProviderKind, typeof claudeDynamicModelsQuery.data> = {
      claudeAgent: claudeDynamicModelsQuery.data,
      codex: codexDynamicModelsQuery.data,
    };

    for (const provider of ["claudeAgent", "codex"] as const) {
      const dynamicModels = dynamicSources[provider]?.models;
      if (dynamicModels && dynamicModels.length > 0) {
        result[provider] = mergeDynamicModelOptions({
          provider,
          staticOptions: staticOptions[provider],
          dynamicModels: dynamicModels.map((model) => ({
            slug: model.slug,
            name: model.name,
          })),
        });
      }
    }

    // Final filter: drop models the current user is not allowed to use.
    for (const provider of ["claudeAgent", "codex"] as const) {
      result[provider] = result[provider].filter((m) =>
        allowedModelKeys.has(`${provider}:${m.slug}`),
      );
    }

    return result;
  }, [settings, claudeDynamicModelsQuery.data, codexDynamicModelsQuery.data, allowedModelKeys]);

  return {
    modelOptionsByProvider,
    visibleManagedAgents,
    providerStatuses: serverConfigQuery.data?.providers ?? EMPTY_PROVIDER_STATUSES,
  };
}
